import React, { useEffect, useState } from "react";
import { Bonus } from "../../../types/bonus";
import ReactModal from "react-modal";
import {
  getDate,
} from "../../../helpers/dateHelper";
import { AppButton } from "../../common/ctaButton/button";
import { BonusDrawIcon } from "./BonusIcon";
import { Raffle } from "../../../types/raffle";
import {
  Box,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import { BonusRafflesField } from "./BonusRafflesField";
import DateTimePicker from 'react-datetime-picker';
import { useDataProvider, useNotify } from "react-admin";
import CreateBonusField from "./CreateBonusField";

interface BonusCreateEditModalProps {
  bonus: Bonus | undefined;
  closeModal: () => void;
  showModal: boolean;
  raffles: Array<Raffle>;
  refresh: () => void;
}

const BonusColors = [
  { text: "Green", key: "green" },
  { text: "Light-green", key: "light-green" },
  { text: "Gray", key: "gray" },
  { text: "Black", key: "black" },
];

export const BonusCreateEditModal = ({
  bonus,
  closeModal,
  showModal,
  raffles,
  refresh,
}: BonusCreateEditModalProps) => {
  const [state, setState] = useState<Bonus>({
    _id: bonus?._id || "",
    pageTitle: bonus?.pageTitle || "",
    profileTitle: bonus?.profileTitle || "",
    metaTitle: bonus?.metaTitle || "",
    metaDescription: bonus?.metaDescription || "",
    price: bonus?.price || "",
    color: bonus?.color || "green",
    startAt: bonus?.startAt || "",
    endAt: bonus?.endAt || "",
    isActive: bonus ? bonus.isActive : false,
    raffles: bonus ? bonus.raffles : [],
  });

  const [errors, setErrors] = useState({
    pageTitle: { status: false, message: "" },
    profileTitle: { status: false, message: "" },
    metaTitle: { status: false, message: "" },
    metaDescription: { status: false, message: "" },
    color: { status: false, message: "" },
    startAt: { status: false, message: "" },
    endAt: { status: false, message: "" },
    price: { status: false, message: "" },
    raffles: { status: false, message: "" },
  });

  const [selectedRaffle, setSelectedRaffle] = useState<string>("");
  const [associatedRaffles, setAssociatedRaffles] = useState<Array<Raffle>>([]);
  const [actualRaffles, setActualRaffles] = useState<Array<Raffle>>([]);

  const [touchState, setTouch] = useState({
    pageTitle: false,
  });

  const notify = useNotify();
  const dataProvider = useDataProvider();

  const handleChange = (e: any) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;
    setTouch({
      ...touchState,
      [name]: true,
    });

    setErrors((state) => ({
      ...errors,
      [name]: { ...state[name], status: false },
    }));

    setState((state) => ({
      ...state,
      [name]: "" + value,
    }));
  };

  const handleChangeDate = (e: any) => {
    const name = e.currentTarget.name;
    const value = e.currentTarget.value;

    setTouch({
      ...touchState,
      [name]: true,
    });

    setErrors(() => ({
      ...errors,
      endAt: { status: false, message: "" },
    }));

    setState((state) => ({
      ...state,
      [name]: "" + value,
    }));

    let endDate = 0;
    let startDate = 0;

    if (name === "endAt" && state.startAt) {
      endDate = value.getTime();
      startDate = new Date(state.startAt).getTime();
      } else if (name === "startAt" && state.endAt) {
        startDate = value.getTime();
        endDate = new Date(state.endAt).getTime();
      }
    
    if (endDate && startDate) {
      if (startDate >= endDate) {
        setErrors({
          ...errors,
          endAt: { status: true, message: "Start date cannot be later than finish date" },
        });
      }
    }
  }

  const trimValueOnBlur = (e) => {
    setState({
      ...state,
      [e.target.name]: state[e.target.name].trim(),
    });
  };

  const handleColorChange = (event) => {
    const value = event.target.value;
    setState({ ...state, color: value });
  };

  const handleChangeBonusStatus = () => {
    setState((state) => ({
      ...state,
      isActive: !state.isActive,
    }));
  };

  const getRaffleList = () => {
    if (bonus && bonus.raffles && bonus.raffles.length) {
      const currentRafflesList: Array<Raffle> = [];
      bonus.raffles.forEach((raffleId) => {
        const currentRaffle = raffles.find((raffle) => raffle._id === raffleId);
        if (currentRaffle) {
          currentRafflesList.push(currentRaffle);
        }
      });
      setAssociatedRaffles(currentRafflesList);
    }
  };

  const setActualRafflesList = () => {
    if (raffles && raffles.length) {
      if (associatedRaffles.length) {
        const actualList = raffles.filter(
          (raffle) => !associatedRaffles.some((item) => item._id === raffle._id)
        );
        setActualRaffles(actualList);
      } else {
        setActualRaffles(raffles);
      }
    }
  };

  useEffect(() => {
    getRaffleList();
  }, [bonus]);

  useEffect(() => {
    setActualRafflesList();
    const raffleIds = associatedRaffles.map(raffle => {
      return raffle._id
    })
    setState(() => ({...state, raffles: raffleIds}))
  }, [associatedRaffles]);

  const handleAddRaffle = () => {
    if (selectedRaffle) {
      const newRaffle = raffles.find((raffle) => raffle._id === selectedRaffle);

      if (newRaffle) {
        setAssociatedRaffles([...associatedRaffles, newRaffle]);
      }

      setErrors((state) => ({
        ...errors,
        raffles: { ...state.raffles, status: false },
      }));
    }
  };

  const handleDeleteRaffle = (id: string) => {
    if (id) {
      const newRafflesList = associatedRaffles.filter(
        (raffle) => raffle._id !== id
      );
      setAssociatedRaffles(newRafflesList);
    }
  };

  const handleSaveBonus = async () => {
    let currentErrors = {};
    Object.keys(state).map((key) => {
      if(!state[key] || !state[key].length) {
        if (key !== "_id" && key !== "isActive") {
          if(key === "raffles" && state[key].length) {
            return;
          }
          currentErrors[key] = { status: true, message: 'Field is required' };
        }
      }
    });

    if (Object.keys(currentErrors).length !== 0 || errors.endAt.status) {
      setErrors({
      ...errors,
      ...currentErrors,
    });
      notify('Please check fields ', 'error');
      return;
    } else {
      const data = {...state};
      Object.keys(state).map((key) => {
        if (!data[key]) {
          delete data[key];
        }
      });
      data.isActive = state.isActive;
      data.startAt = new Date(state.startAt).toUTCString();
      data.endAt = new Date(state.endAt).toUTCString();
      data.price = Number(state.price.replace(/,/g, '')).toLocaleString("en-US");

      if (bonus) {
        try {
          const result: any = await dataProvider.editBonus('/bonusDraw', data);
          if (result.data) {
            notify('Bonus edited', 'info');
            closeModal();
            refresh();
          }
        } catch (error: any) {
          notify(error.data.error, 'error');
        }
      } else {
        try {
          const result: any = await dataProvider.addBonus('/bonusDraw', data);
          if (result.data) {
            notify('Bonus added', 'info');
            closeModal();
            refresh();
          }
        } catch (error: any) {
          notify(error.data.error, 'error');
        }
      }
    }
  }

  return (
    <>
      <ReactModal
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
        appElement={document.getElementById("root")}
        onClose={closeModal}
        isOpen={showModal}
        contentLabel="Inline Styles Modal Example"
        className="globalModalWrapper"
        style={{
          overlay: {
            backgroundColor: "rgba(9, 16, 29, 0.6)",
            zIndex: 10,
            width: "100%",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <div className="couponModalContentWrapper">
          <div className="modalHeader">
            <span>{bonus ? "Edit bonus" : "Create bonus"}</span>
          </div>
          <div className="modalBody modalBody_create">
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Page Title"
                  field={
                    <Box width={"80%"}>
                      <TextField
                        label=""
                        size="small"
                        variant="outlined"
                        type="text"
                        name="pageTitle"
                        error={errors.pageTitle.status}
                        onChange={handleChange}
                        value={state.pageTitle}
                        onBlur={trimValueOnBlur}
                      />
                    </Box>
                  }
                />
                {errors.pageTitle.status && (
                  <span className="fieldErrorMessage">
                    {errors.pageTitle.message}
                  </span>
                )}
              </div>
              <div className="side" style={{ flex: "0 0 120px" }}>
                <CreateBonusField
                  fieldTitle="Active"
                  fieldTemplate="inline"
                  field={
                    <Switch
                      checked={state.isActive}
                      onChange={handleChangeBonusStatus}
                      color="primary"
                      name="checkedB"
                      inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  }
                />
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Profile Title"
                  field={
                    <Box width={"90%"}>
                      <TextField
                        label=""
                        size="small"
                        variant="outlined"
                        type="text"
                        name="profileTitle"
                        error={errors.profileTitle.status}
                        onChange={handleChange}
                        value={state.profileTitle}
                        onBlur={trimValueOnBlur}
                      />
                    </Box>
                  }
                />
                {errors.profileTitle.status && (
                  <span className="fieldErrorMessage">
                    {errors.profileTitle.message}
                  </span>
                )}
              </div>
              <div className="side">
                <CreateBonusField
                  fieldTitle="Meta Title"
                  field={
                    <Box width={"90%"}>
                      <TextField
                        label=""
                        size="small"
                        variant="outlined"
                        type="text"
                        name="metaTitle"
                        error={errors.metaTitle.status}
                        onChange={handleChange}
                        value={state.metaTitle}
                        onBlur={trimValueOnBlur}
                      />
                    </Box>
                  }
                />
                {errors.metaTitle.status && (
                  <span className="fieldErrorMessage">
                    {errors.metaTitle.message}
                  </span>
                )}
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Meta Description"
                  field={
                    <Box width={"100%"}>
                      <TextField
                        label=""
                        size="small"
                        variant="outlined"
                        type="text"
                        name="metaDescription"
                        error={errors.metaDescription.status}
                        onChange={handleChange}
                        value={state.metaDescription}
                        onBlur={trimValueOnBlur}
                      />
                    </Box>
                  }
                />
                {errors.metaDescription.status && (
                  <span className="fieldErrorMessage">
                    {errors.metaDescription.message}
                  </span>
                )}
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Start Date"
                  field={
                    <DateTimePicker
                      value={state.startAt && new Date(state.startAt)}
                      format={"dd-MM-y"}
                      name="startAt"
                      onChange={(value: Date) =>
                        handleChangeDate({
                          currentTarget: { name: "startAt", value },
                        })
                      }
                      disableClock
                      className={errors.startAt.status ? "error" : ""}
                    />
                  }
                />
                {errors.startAt.status && (
                  <div className="dateErrorMessageWrapper">
                    <span className="fieldErrorMessage">
                      {errors.startAt.message}
                    </span>
                  </div>
                )}
              </div>
              <div className="side">
                <CreateBonusField
                  fieldTitle="End Date"
                  field={
                    <DateTimePicker
                      value={state.endAt && new Date(state.endAt)}
                      format={"dd-MM-y"}
                      name="endAt"
                      onChange={(value: Date) =>
                        handleChangeDate({
                          currentTarget: { name: "endAt", value },
                        })
                      }
                      className={errors.endAt.status ? "error" : ""}
                    />
                  }
                />
                {errors.endAt.status && (
                  <div className="dateErrorMessageWrapper">
                    <span className="fieldErrorMessage">
                      {errors.endAt.message}
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Price, £"
                  field={
                    <Box width={"80%"}>
                      <TextField
                        label=""
                        size="small"
                        variant="outlined"
                        type="number"
                        name="price"
                        error={errors.price.status}
                        onChange={handleChange}
                        value={+state.price.replace(/[^+\d]/g, '')}
                        onBlur={trimValueOnBlur}
                      />
                    </Box>
                  }
                />
                {errors.price.status && (
                  <span className="fieldErrorMessage">
                    {errors.price.message}
                  </span>
                )}
              </div>
              <div className="side">
                <CreateBonusField
                  fieldTitle="Color"
                  field={
                    <Box width={"80%"}>
                      <Select
                        id="couponType"
                        variant="outlined"
                        value={state.color}
                        name={"discountType"}
                        onChange={handleColorChange}
                        IconComponent={KeyboardArrowDownIcon}
                      >
                        {BonusColors.map((item) => (
                          <MenuItem key={item.key} value={item.key}>
                            {item.text}
                          </MenuItem>
                        ))}
                      </Select>
                      <div className="bonus-badge">
                        <BonusDrawIcon color={state.color}/>
                      </div>
                    </Box>
                  }
                />
                {errors.price.status && (
                  <span className="fieldErrorMessage">
                    {errors.price.message}
                  </span>
                )}
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Associated Raffles:"
                  field={
                    <Box width={"100%"}>
                      <BonusRafflesField
                        raffles={associatedRaffles}
                        removeRaffle={handleDeleteRaffle}
                        error={errors.raffles.status}
                      />
                    </Box>
                  }
                />
                {errors.raffles.status && (
                  <span className="fieldErrorMessage">
                    {errors.raffles.message}
                  </span>
                )}
              </div>
            </div>
            <div className="fieldsRow">
              <div className="side">
                <CreateBonusField
                  fieldTitle="Select Raffle:"
                  field={
                    <Box width={"90%"}>
                      <Select
                        id="associatedRaffles"
                        variant="outlined"
                        value={selectedRaffle}
                        name={"associatedRaffles"}
                        onChange={(e) =>
                          setSelectedRaffle(e.target.value as string)
                        }
                        IconComponent={KeyboardArrowDownIcon}
                        placeholder="Select Raffle"
                      >
                        {actualRaffles.map((item) => (
                          <MenuItem key={item._id} value={item._id}>
                            {item.title} (
                            {getDate(new Date(item.startAt), false)}
                            )
                            {item.active && (
                              <span className="modal-bonus-details-item__active-label">
                                active
                              </span>
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  }
                />
              </div>
              <div
                className="side"
                style={{
                  flex: "0 0 210px",
                  alignSelf: "flex-end",
                  marginBottom: "10px",
                }}
              >
                <AppButton onClick={handleAddRaffle} text="Add Raffle" />
              </div>
            </div>
            <div className="modalBody__btn">
              <AppButton
                onClick={handleSaveBonus}
                size='long'
                text={bonus ? "Edit bonus" : "Create new Bonus"}
              />
            </div>
          </div>
        </div>
      </ReactModal>
    </>
  );
};
